<template>
  <div class="pdv-checkout-invoices-panel" data-testid="pdv-checkout-invoices-panel">
    <div class="section-label">{{ t('Notas Fiscais') }}</div>

    <div v-if="isLoading" class="panel-loading">
      <i class="pi pi-spinner pi-spin"></i>
      <span>{{ t('Carregando...') }}</span>
    </div>

    <div v-else-if="invoices.length === 0" class="panel-empty" data-testid="pdv-checkout-invoices-empty">
      <span>{{ t('Nenhuma nota fiscal emitida para esta OS') }}</span>
    </div>

    <div v-else class="invoices-list" data-testid="pdv-checkout-invoices-list">
      <div
        v-for="inv in invoices"
        :key="inv.id"
        class="invoice-row"
        :data-testid="`pdv-checkout-invoices-row-${inv.id}`"
      >
        <div class="invoice-info">
          <span class="invoice-type">{{ getDocumentKindLabel(inv.document_kind) }}</span>
          <Tag
            :severity="getInvoiceStatusConfig(inv.status).severity"
            :value="getInvoiceStatusConfig(inv.status).label"
            :data-testid="`pdv-checkout-invoices-status-${inv.id}`"
          />
          <!--
            O NUMERO VEM DE authority_number, NAO DE invoice_number.
            No caminho brasileiro (Focus) invoice_number e SEMPRE nulo: a criacao
            devolve null de proposito, porque o numero da nota e atribuido pela
            autoridade DEPOIS, e quem o grava e o retorno do webhook, em
            authority_number. Conferido nas linhas reais de uma emissao de
            verdade: invoice_number NULL, authority_number '4'/'5'/'6'.
            invoice_number fica como fallback porque e o campo que o caminho
            chileno preenche (la o folio vem na propria emissao e
            authority_number e que fica nulo).
            Sem isto o painel mostra linhas sem numero ao lado da mensagem
            "esta OS ja possui a nota n. 4" — que e exatamente o que esta tela
            existe para tornar visivel.
          -->
          <span v-if="invoiceNumber(inv)" class="invoice-number">#{{ invoiceNumber(inv) }}</span>
        </div>
        <div class="invoice-actions">
          <Button
            v-if="inv.pdf_url"
            icon="pi pi-file-pdf"
            text
            size="small"
            :title="t('Baixar PDF')"
            :data-testid="`pdv-checkout-invoices-pdf-${inv.id}`"
            @click="handleDownloadPdf(inv)"
          />
          <Button
            v-if="inv.xml_url"
            icon="pi pi-file"
            text
            size="small"
            :title="t('Baixar XML')"
            :data-testid="`pdv-checkout-invoices-xml-${inv.id}`"
            @click="handleDownloadXml(inv)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { usePaymentInvoices, type InvoiceListRow } from '../../composables/usePaymentInvoices';
import { log } from '../../utils/DebugLogger';

type Id = number | string;

const props = defineProps<{
  orderId: Id;
}>();

const { t } = useI18n();
const {
  listInvoicesForOrdemServico,
  getInvoiceStatusConfig,
  getDocumentKindLabel,
  downloadPdf,
  downloadXml,
} = usePaymentInvoices();

const isLoading = ref(false);
const invoices = ref<InvoiceListRow[]>([]);

/**
 * O numero da nota, de onde ele REALMENTE esta em cada caminho.
 *
 * Brasil (Focus): a emissao devolve invoice_number NULO de proposito — o numero
 * e atribuido pela autoridade depois, e chega pelo retorno do webhook em
 * authority_number. Chile (DTE): o folio vem na propria emissao, em
 * invoice_number, e authority_number fica nulo.
 *
 * Ler so invoice_number, como esta tela fazia, deixava toda nota brasileira sem
 * numero — inclusive a que a mensagem de bloqueio cita pelo numero.
 */
const invoiceNumber = (inv: InvoiceListRow): string | null => {
  const authority = (inv as { authority_number?: string | null }).authority_number;
  if (authority !== null && authority !== undefined && String(authority).trim() !== '') {
    return String(authority);
  }
  const legacy = inv.invoice_number;
  return legacy !== null && legacy !== undefined && String(legacy).trim() !== '' ? String(legacy) : null;
};

const loadInvoices = async (): Promise<void> => {
  log('PDVCheckoutInvoicesPanel', 'loadInvoices', 'Loading invoices for order', props.orderId);
  isLoading.value = true;
  try {
    invoices.value = await listInvoicesForOrdemServico(Number(props.orderId));
  } finally {
    isLoading.value = false;
  }
};

const handleDownloadPdf = (inv: InvoiceListRow): void => {
  log('PDVCheckoutInvoicesPanel', 'handleDownloadPdf', 'Downloading PDF', inv.id);
  downloadPdf(inv.id, inv.pdf_url);
};

const handleDownloadXml = (inv: InvoiceListRow): void => {
  log('PDVCheckoutInvoicesPanel', 'handleDownloadXml', 'Downloading XML', inv.id);
  downloadXml(inv.id, inv.xml_url);
};

// The emission handler in PDVCheckoutForm already fires 'pdv.saved' on success
// (same event PDVCheckoutRecebimentosPanel refreshes on) — reuse it instead of
// inventing a dedicated invoices.refresh event.
const handleRefresh = (): void => {
  loadInvoices();
};

onMounted(() => {
  loadInvoices();
  eventBus.on('pdv.saved', handleRefresh);
});

onBeforeUnmount(() => {
  eventBus.off('pdv.saved', handleRefresh);
});
</script>

<style scoped>
.pdv-checkout-invoices-panel {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--p-surface-200);
}

.section-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.panel-loading,
.panel-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  color: var(--p-text-muted-color);
  font-size: 0.85rem;
}

.invoices-list {
  margin-bottom: 4px;
}

.invoice-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  gap: 8px;
}

.invoice-row:nth-child(odd) {
  background: var(--p-surface-50);
}

.invoice-info {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.invoice-type {
  font-weight: 600;
  font-size: 0.8rem;
}

.invoice-number {
  color: var(--p-text-muted-color);
  font-size: 0.8rem;
}

.invoice-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
</style>
